// Video Call Hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { videoCallService } from '../services/videoCallService';
import { InitiateCallRequest, AnswerCallRequest, EndCallRequest } from '../types';

// Query keys
export const videoCallKeys = {
  all: ['video-calls'] as const,
  sessions: () => [...videoCallKeys.all, 'sessions'] as const,
  callHistory: (params?: any) => [...videoCallKeys.sessions(), 'history', params] as const,
  permissions: () => [...videoCallKeys.all, 'permissions'] as const,
  logs: () => [...videoCallKeys.all, 'logs'] as const,
  session: (id: number) => [...videoCallKeys.sessions(), id] as const,
};

// Hooks for video call sessions
export const useVideoCallSessions = () => {
  return useQuery({
    queryKey: videoCallKeys.sessions(),
    queryFn: videoCallService.getSessions,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useCallHistory = (params?: { robot_id?: string; museum_code?: string; status?: string }) => {
  return useQuery({
    queryKey: videoCallKeys.callHistory(params),
    queryFn: () => videoCallService.getCallHistory(params),
  });
};

export const useCallSession = (id: number) => {
  return useQuery({
    queryKey: videoCallKeys.session(id),
    queryFn: () => videoCallService.getCallSession(id),
    enabled: !!id,
  });
};

// Hooks for call permissions
export const useCallPermissions = () => {
  return useQuery({
    queryKey: videoCallKeys.permissions(),
    queryFn: videoCallService.getPermissions,
  });
};

// Hooks for call logs
export const useCallLogs = () => {
  return useQuery({
    queryKey: videoCallKeys.logs(),
    queryFn: videoCallService.getCallLogs,
  });
};

// Mutation hooks
export const useInitiateCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InitiateCallRequest) => videoCallService.initiateCall(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoCallKeys.sessions() });
    },
  });
};

export const useAnswerCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AnswerCallRequest) => videoCallService.answerCall(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoCallKeys.sessions() });
    },
  });
};

export const useEndCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EndCallRequest) => videoCallService.endCall(data),
    onSuccess: () => {
      // Invalidate and refetch all call-related queries
      queryClient.invalidateQueries({ queryKey: videoCallKeys.sessions() });
      queryClient.invalidateQueries({ queryKey: videoCallKeys.callHistory() });
    },
  });
};
